import Image from "next/image";
import { cn } from "@/lib/utils";

interface DhlShippingBadgeProps {
	/** Text label shown next to the DHL logo */
	label?: string;
	/** Visual variant: "pill" (bordered card like payment icons), "inline" (minimal, for trust bars) */
	variant?: "pill" | "inline";
	/** Size preset */
	size?: "sm" | "md";
	className?: string;
}

/**
 * DHL shipping partner badge — used as a trust signal across PDP, cart, PLP, and footer.
 * Renders the DHL logo at a size consistent with PaymentIcons.
 */
export function DhlShippingBadge({ label, variant = "pill", size = "sm", className }: DhlShippingBadgeProps) {
	const logoHeight = size === "sm" ? "h-5" : "h-6";
	const logoWidth = size === "sm" ? "w-[3.5rem]" : "w-[4.25rem]";

	if (variant === "inline") {
		return (
			<span className={cn("flex items-center gap-1.5", className)}>
				<Image
					src="/DHL-Shipping.png"
					alt="DHL"
					width={52}
					height={32}
					className={cn("object-contain", logoHeight, "w-auto")}
					unoptimized
				/>
				{label && <span className="text-xs text-muted-foreground">{label}</span>}
			</span>
		);
	}

	return (
		<div className={cn("flex items-center gap-2", className)}>
			{label && (
				<span className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-muted-foreground">
					{label}
				</span>
			)}
			<div
				className={cn(
					"flex items-center justify-center rounded-md border border-border bg-white shadow-[0_2px_6px_-3px_rgba(8,39,74,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_12px_-4px_rgba(8,39,74,0.25)]",
					size === "sm" ? "h-8 px-2.5" : "h-10 px-3",
				)}
			>
				<Image
					src="/DHL-Shipping.png"
					alt="DHL Shipping"
					width={60}
					height={36}
					className={cn("object-contain", logoHeight, logoWidth)}
					unoptimized
				/>
			</div>
		</div>
	);
}
